import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MEDAL = {
  gold: { color: "#FFD700", title: "GOLD MEDAL", emoji: "🥇", sub: "CHAMPION" },
  silver: { color: "#C0C0C0", title: "SILVER MEDAL", emoji: "🥈", sub: "RUNNER-UP" },
  bronze: { color: "#CD7F32", title: "BRONZE MEDAL", emoji: "🥉", sub: "THIRD PLACE" },
};

export function playFanfare(medal) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    if (ctx.state === "suspended") ctx.resume();
    const base = medal === "gold" ? 523.25 : medal === "silver" ? 466.16 : 415.3;
    const seq = [[0, 1], [0.18, 1.25], [0.36, 1.5], [0.54, 2], [0.95, 1.5], [1.1, 2]];
    seq.forEach(([t, mult], i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "triangle";
      o.frequency.value = base * mult;
      const at = ctx.currentTime + t;
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.22, at + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, at + (i >= 4 ? 1.1 : 0.34));
      o.connect(g);
      g.connect(ctx.destination);
      o.start(at);
      o.stop(at + 1.3);
    });
    setTimeout(() => ctx.close(), 4000);
  } catch { /* audio unavailable */ }
}

function Fireworks({ color }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    const ctx = cv.getContext("2d");
    let w, h, raf;
    const parts = [];
    const size = () => { w = cv.width = window.innerWidth; h = cv.height = window.innerHeight; };
    size();
    window.addEventListener("resize", size);
    const colors = [color, color, "#ffffff", "#BEF264", "#22D3EE"];
    const burst = () => {
      const x = Math.random() * w * 0.8 + w * 0.1;
      const y = Math.random() * h * 0.45 + h * 0.08;
      const c = colors[Math.floor(Math.random() * colors.length)];
      for (let i = 0; i < 64; i++) {
        const a = (Math.PI * 2 * i) / 64;
        const sp = 1.8 + Math.random() * 4.2;
        parts.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 65 + Math.random() * 35, c });
      }
    };
    burst(); burst(); burst();
    const iv = setInterval(burst, 650);
    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        p.x += p.vx; p.y += p.vy; p.vy += 0.045; p.life -= 1;
        if (p.life <= 0) { parts.splice(i, 1); continue; }
        ctx.globalAlpha = Math.min(1, p.life / 45);
        ctx.fillStyle = p.c;
        ctx.fillRect(p.x, p.y, 2.6, 2.6);
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); clearInterval(iv); window.removeEventListener("resize", size); };
  }, [color]);
  return <canvas ref={ref} className="absolute inset-0 pointer-events-none" />;
}

const ordinal = (n) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : { 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;

export default function MedalCelebration() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const cur = queue[0];

  useEffect(() => {
    if (!user) { setQueue([]); return; }
    api.get("/player/celebrations").then(({ data }) => setQueue(data || [])).catch(() => {});
  }, [user?.id]); // eslint-disable-line

  useEffect(() => {
    if (cur?.medal) playFanfare(cur.medal);
  }, [cur?.tournament_id, cur?.label, cur?.medal]); // eslint-disable-line

  if (!cur) return null;
  const dismiss = () => {
    api.post("/player/celebrations/ack", { tournament_id: cur.tournament_id, label: cur.label }).catch(() => {});
    setQueue((q) => q.slice(1));
  };

  if (!cur.medal) {
    return (
      <div className="fixed bottom-4 right-4 z-[110] max-w-sm border border-pb-cyan/50 bg-pb-surface shadow-2xl p-4 animate-fade-up" data-testid="playoff-run-note">
        <div className="text-[10px] uppercase tracking-[0.25em] text-pb-cyan font-bold mb-1">🏆 Great Run!</div>
        <p className="text-sm text-zinc-200 mb-1">You made the playoffs in <span className="font-bold">{cur.label}</span> at {cur.tournament} — finished {ordinal(cur.rank)}{cur.total ? ` of ${cur.total}` : ""}.</p>
        <p className="text-[11px] text-zinc-500 mb-3">Every medal round sharpens your game — the podium is next.</p>
        <button data-testid="playoff-note-dismiss" onClick={dismiss}
          className="w-full border border-pb-cyan/60 text-pb-cyan font-bold uppercase tracking-widest text-[10px] py-2 hover:bg-pb-cyan hover:text-black transition-colors">
          Thanks — Got It
        </button>
      </div>
    );
  }

  const m = MEDAL[cur.medal];
  return (
    <div className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center p-4" data-testid="medal-celebration"
      onClick={() => playFanfare(cur.medal)}>
      <Fireworks color={m.color} />
      <div className="relative text-center max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="text-7xl mb-4 animate-bounce">{m.emoji}</div>
        <div className="font-display font-black uppercase text-4xl sm:text-5xl mb-1 tracking-tight" style={{ color: m.color, textShadow: `0 0 40px ${m.color}88` }}>
          You Won {m.title.split(" ")[0]}!
        </div>
        <div className="text-[11px] font-black uppercase tracking-[0.4em] mb-4" style={{ color: m.color }}>{m.sub}</div>
        <div className="border p-4 mb-5 bg-black/60" style={{ borderColor: `${m.color}66` }}>
          <div className="font-display font-bold uppercase text-lg text-white">{cur.tournament}</div>
          <div className="text-xs uppercase tracking-widest text-zinc-400 mt-0.5">{cur.label}{cur.date ? ` · ${cur.date}` : ""}</div>
          <div className="text-sm mt-2" style={{ color: m.color }}>You finished {ordinal(cur.rank)}{cur.total ? ` of ${cur.total}` : ""} — it's on your record and your medal wall.</div>
        </div>
        <div className="flex items-center justify-center gap-3">
          <button data-testid="celebration-replay" onClick={() => playFanfare(cur.medal)}
            className="border border-zinc-600 text-zinc-300 font-bold uppercase tracking-widest text-[10px] px-4 py-2.5 hover:border-white hover:text-white transition-colors">
            🔊 Replay Fanfare
          </button>
          <button data-testid="celebration-dismiss" onClick={dismiss}
            className="font-black uppercase tracking-widest text-xs px-6 py-2.5 text-black active:scale-95 transition-transform"
            style={{ background: m.color }}>
            Celebrate & Continue
          </button>
        </div>
      </div>
    </div>
  );
}
